import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, Search, Star } from 'lucide-react';
import { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import type { FulfillmentCommentRow } from '@/infrastructure/FulfillmentAdminClient';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import { OperatorIdentity } from '@/infrastructure/OperatorIdentity';
import { useAuth } from '@/providers/AuthProvider';
import { ROUTES } from '@/routes/routes';
import { kstTimestamp } from '@/utils/fulfillmentTime';
import { withMentions } from '@/pages/fulfillment/components/mentionText';
import './commentsHub.css';

/**
 * The canonical hub copy [G-7]. These are a byte-exact cross-page contract — eight screens shipped
 * eight wordings before 2026-08-03, so a local variant here re-opens that fork.
 */
const HUB_MENTIONS_HEADER = 'Comments mentioning me · Click to open the order';
const HUB_SAVED_HEADER = 'Saved comments · Click to open the order';
const HUB_UNSTAR_HINT = 'Unstar to remove from the list';
const HUB_MARK_ALL_READ = 'Mark all read';
const HUB_EMPTY_SEARCH = 'No matching comments';
const HUB_CLICK_THROUGH = 'Click to open the order';

const PAGE_SIZE = 50;

/**
 * How often an open hub re-reads its list and a closed one its badge, while the tab is in front (SSD-7209,
 * decision D6): a colleague's new mention shows without a reload. A hidden tab does not poll, and a tick that
 * lands while the last read is still in flight is skipped rather than queued behind it.
 */
export const HUB_REFRESH_MS = 30_000;

/** A full page means the server stopped at the limit, so the count is a floor rather than the total. */
function searchResultsHeader(resultCount: number): string {
  const count =
    resultCount >= PAGE_SIZE ? `${PAGE_SIZE}+ results` : `${resultCount} result${resultCount === 1 ? '' : 's'}`;
  return `${count} · ${HUB_CLICK_THROUGH}`;
}

type Pane = 'mentions' | 'saved';

/**
 * The cross-order comments hub [G-7]. It holds the comments that name the operator and the ones they
 * starred, and searches across every comment rather than filtering the page already fetched — the term
 * goes to the server, so a comment on an order nobody has opened is still found. A term typed on the
 * Mentions pane reaches every order and inbound-request comment, not only the ones naming the operator
 * [SSD-5624]; on the Saved pane it narrows the starred list.
 *
 * The star, the badge and the read watermark are per-operator, and the API key authenticates the
 * dashboard rather than the person — so the operator travels in the request, the way V1 sends
 * `auditEntry.operator`.
 */
export interface CommentsHubProps {
  /** The bar it sits on decides the outline: a dark navbar needs a light one to be seen at all. */
  variant?: string;
  /** On the navbar the word costs the width the avatar and Logout need, so the glyph goes alone. */
  showLabel?: boolean;
}

export function CommentsHub({ variant = 'outline-secondary', showLabel = true }: CommentsHubProps) {
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const { user } = useAuth();
  const operator = OperatorIdentity.handleOf(user?.email, user?.name);

  const [open, setOpen] = useState(false);
  const panelId = useId();
  const [pane, setPane] = useState<Pane>('mentions');
  const [search, setSearch] = useState('');
  const [comments, setComments] = useState<FulfillmentCommentRow[]>([]);
  const [resultCount, setResultCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // A star or a read-all changes what the server would answer, so the pane refetches rather than guessing.
  const [refreshedAt, setRefreshedAt] = useState(0);
  // Read on press, before the toggle takes the cursor for itself: on a desk screen this is the scan box.
  const cursorBeforePress = useRef<Element | null>(null);
  const returnCursorTo = useRef<Element | null>(null);
  const container = useRef<HTMLDivElement | null>(null);
  // Every keystroke reloads; only the newest answer may land, or a slow broad search overwrites a narrow one.
  const latestLoad = useRef(0);
  // A badge read started before mark-all-read must not bring back the count it just cleared.
  const latestBadgeRead = useRef(0);
  const markingAllRead = useRef(false);
  // How many list and badge reads are still out, so a poll tick never stacks another behind them. Each read takes
  // its own count off when it settles, whichever finishes first, so one read cannot clear the other's mark.
  const listReadsOut = useRef(0);
  const badgeReadsOut = useRef(0);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  /**
   * Closing from the hub itself hands the cursor back to whatever held it before the hub opened. Left on
   * the toggle, the next scan's characters went nowhere and its Enter opened the hub again [SSD-6928 8d].
   * A click elsewhere or a page change already put the cursor where the operator wanted it.
   */
  const close = useCallback((handCursorBack: boolean) => {
    setOpen(false);
    const back = returnCursorTo.current;
    returnCursorTo.current = null;
    if (!handCursorBack) {
      return;
    }
    if (back instanceof HTMLElement && back !== document.body && back.isConnected) {
      back.focus();
    } else if (document.activeElement instanceof HTMLElement) {
      // Nothing to go back to; a toggle that keeps the cursor would reopen on the next Enter.
      document.activeElement.blur();
    }
  }, []);

  const toggle = () => {
    const pressedFrom = cursorBeforePress.current ?? document.activeElement;
    cursorBeforePress.current = null;
    if (!open) {
      returnCursorTo.current = pressedFrom;
      setOpen(true);
      return;
    }
    close(true);
  };

  // An open hub covers the screen behind it, so it gives way to Esc and to a click anywhere else.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close(true);
      }
    };
    const onPointerDown = (event: MouseEvent) => {
      if (event.target instanceof Node && !container.current?.contains(event.target)) {
        close(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, close]);

  // The hub lives on the navbar, which outlasts the page under it; a new page should not open under an old panel.
  const shownOn = useRef(pathname);
  useEffect(() => {
    if (shownOn.current === pathname) {
      return;
    }
    shownOn.current = pathname;
    close(false);
  }, [pathname, close]);

  const markAllRead = useCallback(async () => {
    if (!operator) {
      return;
    }
    const markLoadId = ++latestLoad.current;
    latestBadgeRead.current += 1;
    markingAllRead.current = true;
    // A search on screen reloads without the mention count, so the badge cannot wait for the refetch.
    setUnreadCount(0);
    try {
      await client.markCommentsRead(operator);
    } catch {
      // Nothing may have been marked, so the badge goes back to what the server still counts.
      try {
        const page = await client.searchComments({ limit: 1, operator, mentions: operator });
        if (markLoadId === latestLoad.current) {
          setUnreadCount(page.unreadMentionTotal ?? page.unreadMentionCount);
        }
      } catch (error) {
        console.error('Error reconciling fulfillment comment reads:', error);
      }
    } finally {
      markingAllRead.current = false;
      latestBadgeRead.current += 1;
      setRefreshedAt((n) => n + 1);
    }
  }, [client, operator]);

  /**
   * Opening one mention is what reads it (10/17 decision, in place of read-on-open): the watermark moves up to that
   * comment, so older mentions read with it and newer ones stay unread. A row that is not an unread mention moves nothing.
   */
  const markReadUpTo = useCallback(
    async (comment: FulfillmentCommentRow) => {
      if (!operator || comment.mentionsMe !== true || comment.unread !== true || !comment.commentedAt) {
        return;
      }
      // The same fences mark-all-read uses: a list or badge read already in flight must not bring the old count back.
      latestLoad.current += 1;
      latestBadgeRead.current += 1;
      markingAllRead.current = true;
      // This one is read now; the refetch settles how many older ones went with it.
      setUnreadCount((count) => Math.max(0, count - 1));
      try {
        await client.markCommentsRead(operator, comment.commentedAt);
      } catch (error) {
        console.error('Error marking a fulfillment comment read:', error);
      } finally {
        markingAllRead.current = false;
        latestBadgeRead.current += 1;
        setRefreshedAt((n) => n + 1);
      }
    },
    [client, operator],
  );

  /**
   * A quiet read is the 30 s beat on an open list: the rows stay on screen and are swapped in place when the answer
   * lands, so the list keeps its scroll and a click mid-read still lands on a row. A quiet read that fails leaves
   * the rows as they were; only a read the operator asked for shows the spinner or the error.
   */
  const load = useCallback(
    async (quiet = false) => {
      // Without an operator the panes have nothing to scope by, and an unscoped read would list
      // everyone's comments under "mentioning me".
      if (!operator) {
        setComments([]);
        setUnreadCount(0);
        return;
      }
      const loadId = ++latestLoad.current;
      listReadsOut.current += 1;
      if (!quiet) {
        setLoading(true);
        setFailed(false);
      }
      try {
        const term = search.trim();
        // The Mentions pane lists the operator's mentions until a term is typed; the term then searches every comment.
        const mentionsOnly = pane === 'mentions' && term === '';
        const page = await client.searchComments({
          limit: PAGE_SIZE,
          operator,
          ...(term !== '' ? { search: term } : {}),
          ...(pane === 'saved' ? { savedOnly: true } : {}),
          ...(mentionsOnly ? { mentions: operator } : {}),
        });
        if (loadId !== latestLoad.current) {
          return;
        }
        // An answer on screen ends an earlier failure too, so a quiet beat that gets through brings the rows back.
        setFailed(false);
        setComments(page.comments);
        setResultCount(page.resultCount);
        // The total holds whatever the pane or term; without it only a mentions read carries the number,
        // since a search or the Saved pane answers 0 for the page-scoped count.
        if (page.unreadMentionTotal !== undefined) {
          setUnreadCount(page.unreadMentionTotal);
        } else if (mentionsOnly) {
          setUnreadCount(page.unreadMentionCount);
        }
      } catch (error) {
        if (loadId !== latestLoad.current) {
          return;
        }
        console.error('Error searching fulfillment comments:', error);
        if (!quiet) {
          setFailed(true);
        }
      } finally {
        listReadsOut.current -= 1;
        if (loadId === latestLoad.current) {
          setLoading(false);
        }
      }
    },
    [client, operator, pane, search],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    void load();
  }, [open, load, refreshedAt]);

  // The open list and the closed badge are both re-read on the beat; a hidden tab waits for its next visible tick.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || listReadsOut.current > 0 || badgeReadsOut.current > 0) {
        return;
      }
      if (open) {
        void load(true);
      } else {
        setRefreshedAt((n) => n + 1);
      }
    }, HUB_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [open, load]);

  /**
   * A badge that only counts once the pane is open tells the operator what they already went to
   * look at. The server totals every unread mention, not just the rows it returns, so the closed hub
   * asks for one row and takes the total.
   */
  useEffect(() => {
    if (open || !operator) {
      return;
    }
    let ignore = false;
    const badgeReadId = ++latestBadgeRead.current;
    badgeReadsOut.current += 1;
    client
      .searchComments({ limit: 1, operator, mentions: operator })
      .then((page) => {
        if (!ignore && !markingAllRead.current && badgeReadId === latestBadgeRead.current) {
          setUnreadCount(page.unreadMentionTotal ?? page.unreadMentionCount);
        }
      })
      // The badge is not worth an error on a screen that was doing something else.
      .catch(() => undefined)
      .finally(() => {
        badgeReadsOut.current -= 1;
      });
    return () => {
      ignore = true;
    };
  }, [client, open, operator, refreshedAt]);

  const toggleStar = async (comment: FulfillmentCommentRow) => {
    if (comment.orderId === null) {
      return;
    }
    if (!operator || !comment.commentedAt) {
      return;
    }
    await client.setCommentStar({
      operator,
      orderId: comment.orderId,
      commentedAt: comment.commentedAt,
      starred: !comment.starred,
    });
    setRefreshedAt((n) => n + 1);
  };

  /** The red dot: a mention of the operator the server still counts as unread (SSD-7435). */
  const unreadDot = (comment: FulfillmentCommentRow): boolean => comment.mentionsMe === true && comment.unread === true;

  const paneHeader = pane === 'saved' ? HUB_SAVED_HEADER : HUB_MENTIONS_HEADER;
  // The count belongs to the answer on screen, so it waits for the search the operator just typed.
  const header =
    search.trim() === '' || failed
      ? paneHeader
      : loading
        ? `Searching · ${HUB_CLICK_THROUGH}`
        : searchResultsHeader(resultCount);

  return (
    <div ref={container} className="comments-hub">
      <Button
        size="sm"
        variant={variant}
        className="hub-trigger"
        aria-label="Comments"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onMouseDown={() => {
          cursorBeforePress.current = document.activeElement;
        }}
        onClick={toggle}
      >
        <MessageSquare size={14} className={showLabel ? 'me-1' : ''} />
        {showLabel && 'Comments'}
        {unreadCount > 0 && <span className="hub-badge">{unreadCount}</span>}
      </Button>

      {open && (
        <div id={panelId} data-testid="comments-hub-panel" className="hub-panel">
          <div className="hub-head">
            <div className="hub-tabs">
              <button
                type="button"
                className={pane === 'mentions' ? 'hub-tab on' : 'hub-tab'}
                aria-pressed={pane === 'mentions'}
                onClick={() => setPane('mentions')}
              >
                @ Mentions
                {unreadCount > 0 && <span className="hub-tab-n">({unreadCount})</span>}
              </button>
              <button
                type="button"
                className={pane === 'saved' ? 'hub-tab on' : 'hub-tab'}
                aria-pressed={pane === 'saved'}
                onClick={() => setPane('saved')}
              >
                ★ Saved
              </button>
            </div>
            {pane === 'mentions' && (
              <button type="button" className="hub-link" onClick={() => void markAllRead()}>
                {HUB_MARK_ALL_READ}
              </button>
            )}
          </div>

          <div className="hub-search">
            <Search size={14} className="hub-search-ic" aria-hidden="true" />
            <input
              className="hub-search-in"
              type="search"
              aria-label="Search comments"
              placeholder="Search every comment"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className="hub-caption">{header}</div>
          {pane === 'saved' && <div className="hub-hint">{HUB_UNSTAR_HINT}</div>}

          {loading && (
            <div className="hub-loading">
              <Spinner animation="border" size="sm" role="status" aria-label="Loading comments" />
            </div>
          )}
          {failed && !loading && (
            <div className="hub-error" role="alert">
              Could not load comments.
            </div>
          )}
          {!loading && !failed && comments.length === 0 && (
            <div className="hub-empty">
              <MessageSquare size={20} aria-hidden="true" />
              <span>{HUB_EMPTY_SEARCH}</span>
            </div>
          )}

          {!loading && !failed && comments.length > 0 && (
            <ul className="hub-list">
              {comments.map((comment, index) => {
                const unread = unreadDot(comment);
                const target =
                  comment.orderId !== null
                    ? ROUTES.fulfillment.orderDetail(comment.orderId)
                    : ROUTES.fulfillment.inboundRequestDetail(comment.docId ?? '');
                return (
                  <li
                    key={`${comment.orderId ?? comment.docId}-${comment.commentedAt}-${index}`}
                    className={unread ? 'hub-row unread' : 'hub-row'}
                    onClick={(event) => {
                      const where = event.target as HTMLElement;
                      // The star keeps its own handler, and pressing it is not a visit.
                      if (where.closest('button')) {
                        return;
                      }
                      // Opening the order is what reads the mention (10/17 decision): the number link does its own
                      // navigating, new tab included, and the watermark moves up to this comment either way.
                      if (where.closest('a')) {
                        void markReadUpTo(comment);
                        return;
                      }
                      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                        return;
                      }
                      void markReadUpTo(comment);
                      navigate(target);
                    }}
                  >
                    <div className="hub-row-top">
                      {unread && (
                        <span
                          className="hub-dot"
                          role="img"
                          aria-label="Unread mention"
                          title="Unread mention"
                          data-testid="unread-dot"
                        />
                      )}
                      {comment.orderId !== null ? (
                        <Link className="hub-order" to={target}>
                          {comment.orderId}
                        </Link>
                      ) : (
                        <Link className="hub-order" to={target}>
                          {comment.inboundNo}
                        </Link>
                      )}
                      <span className="hub-time">{kstTimestamp(comment.commentedAt)}</span>
                      {/* An arrival note has no order id to key a mark by, so it carries no star at all. */}
                      {comment.starrable !== false && comment.orderId !== null && (
                        <button
                          type="button"
                          className={comment.starred ? 'hub-star on' : 'hub-star'}
                          aria-label={`${comment.starred ? 'Unstar' : 'Star'} comment on order ${comment.orderId}`}
                          aria-pressed={comment.starred}
                          onClick={() => void toggleStar(comment)}
                        >
                          <Star size={14} fill={comment.starred ? 'currentColor' : 'none'} />
                        </button>
                      )}
                    </div>
                    <div className="hub-text">{withMentions(comment.text ?? '')}</div>
                    <div className="hub-by">
                      <span className="hub-avatar" aria-hidden="true">
                        {(comment.author ?? '?').trim().charAt(0) || '?'}
                      </span>
                      {comment.author ?? '—'}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
